import React, { useState, useEffect, useRef } from "react";
import { Button } from "components/globals/ui/input/Button";
import Spinner from "components/globals/ui/Spinner";
import { FaAngleDown } from "react-icons/fa";
import * as Switch from "@radix-ui/react-switch";
import { Link } from "react-router-dom";
import clsx from "clsx";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export const PSInputGroup = function ({ children }) {
  return (
    <>
      <div className="my-sm">{children}</div>
    </>
  );
};

export const PSLabel = function ({ value, required }) {
  return (
    <>
      <div className=" uppercase text-[12px] mb-2">
        <span>{value}</span>
        {required && <span className="text-warning inline-block ml-1">*</span>}
      </div>
    </>
  );
};

export function PSInputBase({
  onChange,
  name,
  label,
  type = "text",
  required = false,
  context,
  value,
  dark = false,
}) {
  const handleChange = (e) => {
    if (onChange) {
      onChange(context, e.target.value);
    }
  };

  return (
    <PSInputGroup>
      <PSLabel value={label} required={required} />
      <input
        className={clsx(
          "border-[1px] border-inputBorder rounded-lg py-3 px-3 block w-full",
          dark ? "bg-transparent border-white" : "bg-white border-inputBorder",
        )}
        name={name}
        type={type}
        value={value}
        required={required}
        onChange={handleChange}
      />
    </PSInputGroup>
  );
}

export function PSWysiwyg({ onChange, name, label, context, value = '' }) {
  const [text, setText] = useState("");

  useEffect(() => {
    setText(value || "");
  }, [value]);

  const handleTextChange = (val) => {
    setText(val);
    onChange(context, val);
  };

  return (
    <div className="my-5">
      <PSLabel value={label} />
      <ReactQuill
        id={name}
        value={text}
        onChange={handleTextChange}
        theme="snow"
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid var(--input-border)',
        }}
      />
    </div>
  )
}

export function VideoUpload({onChange, name, label, context, value = ''}) {
  const MAX_FILE_SIZE_MB = 200;
  const MAX_DURATION_SECONDS = 60;
  const [video, setVideo] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      alert("File size exceeds 200MB limit.");
      return;
    }

    const videoElement = document.createElement("video");
    videoElement.preload = "metadata";

    videoElement.onloadedmetadata = () => {
      window.URL.revokeObjectURL(videoElement.src);
      const duration = videoElement.duration;

      if (duration > MAX_DURATION_SECONDS) {
        alert("Video duration exceeds 60 seconds.");
        return;
      }

      // Passed both checks
      setPreview(URL.createObjectURL(file));
      onChange(context, file);
    };

    videoElement.onerror = () => {
      alert("Failed to load video metadata. Please try a different file.");
    };

    videoElement.src = URL.createObjectURL(file);
  };


  const removeVideo = (e) => {
    onChange("video_id", '');
    setVideo("");
  }
  
  useEffect(() => {
    setVideo(value || "");
  }, [value]);

  return (
    <div className="my-5">
      <PSLabel value={label} />

      {video?.length > 0 ? (
        <div className="bg-black inline-block text-white p-3 px-9 text-center rounded-default text-black ">
          <p className="mb-3">Video Set</p>
          <Button variant="destructive" onClick={removeVideo} >Remove</Button>
        </div>
      ) : (
        <>
          <label htmlFor={name} className="inline-block text-black border-[2px] border-black  py-2 px-4 rounded-lg cursor-pointer">
          Upload Video
        </label>
        <input
          type="file"
          accept="video/*"
          id={name}
          onChange={handleFileChange}
          className="hidden"
        />

        <p className="mt-3  text-[11px] text-guards">Video limitations: 50mb max and no longer than 1 minute.</p>
        </>
      )}
     
      {preview && <video src={preview || video} controls width="400" className="mt-2" />}
    </div>
  );
}

export function PSFormControls({
  data,
  loading,
  edit,
  label,
  path,
  onSubmitForm,
}) {
  return (
    <div className="
      fixed
      bottom-0
      left-0
      w-full
      px-[40px]
      py-3
      z-[10]
      bg-secondary
      border-t-[1px]
      border-inputBorder">
      <div className="flex justify-end">
        <div className="flex items-center">
          {edit && (
            <Link
              to={`/${path}/${data?.entry?.internal_id}`}
              className="mr-2"
            >
              <Button variant="outline">Cancel</Button>
            </Link>
          )}
          <PSSubmit
            onClick={onSubmitForm}
            loading={loading}
            label={
              loading
                ? edit
                  ? "Updating..."
                  : "Creating..."
                : edit
                ? `Update ${label}`
                : `Create ${label}`
            }
            disabled={loading}
          />
        </div>    
      </div>
    </div>
  );
};

export function PSSubmit({ loading, label, disabled, onClick }) {
  return (
    <>
      <div className="relative py-3">
        <Button variant="primary" disabled={disabled} onClick={onClick}>
          {loading ? <div className="px-md"><Spinner /></div> : label}
        </Button>
      </div>
    </>
  );
}

export function PSSelect({
  label,
  firstItemLabel,
  children,
  name,
  onChange,
  value,
}) {
  const [selectedValue, setSelectedValue] = useState(value || "");

  useEffect(() => {
    setSelectedValue(value || "");
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setSelectedValue(newValue);
    onChange(name, newValue);
  };

  return (
    <>
      <PSLabel value={label} />
      <div className="relative">
        <FaAngleDown
          className="
            absolute 
            z-[2] 
            right-4 
            top-2 
            translate-y-1/2"
        />
        <select
          name={name}
          value={selectedValue}
          className="
            bg-white 
            border-[1px] 
            border-inputBorder
            rounded-lg
            py-3 pl-3 pr-10
            block
            w-full
            appearance-none
            relative
          "
          onChange={handleChange}
        >
          <option value="">--- {firstItemLabel} ---</option>
          {children}
        </select>
      </div>
    </>
  );
}

const PostCategoryContainer = ({
  heading, 
  children
}) => {
  return (
    <div className="mb-6 pb-6 border-b-[1px] border-b-[#e0e0e0]">
      <h3 className=" text-sm md:text-md mb-3 md:mb-6">{heading}</h3>
      <div className="my-2 grid grid-cols-2 md:grid-cols-3 gap-3">
        {children}
      </div>
    </div>
  )
}

const PostCategoryItem = ({item, onInputChange, check, inputKey}) => {
  return (
    <div 
      onClick={() => onInputChange(inputKey, item.key)}
      className={clsx(
        "cursor-pointer border-[1px] border-border",
        "p-2 md:p-3 text-[11px] md:text-sm rounded-default text-grey ",
        check === item.key && "bg-brg text-white border-brg"
      )}
    >
      {item.label}
    </div>
  )
}



export function PostTypes({ data, onInputChange, postType }) {
  return (
    <PostCategoryContainer heading="Type">
      {data.map((type) => (
        <PostCategoryItem 
          key={type.key}
          onInputChange={onInputChange}
          check={postType}
          item={type}
          inputKey="type"
        />
      ))}
    </PostCategoryContainer>
  );
}

export function PostCategories({
  data,
  postType,
  onInputChange,
  postCategory,
}) {
  const filteredItems = data
    .filter((category) => category.type === postType)
    .flatMap((category) => category.items);

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <PostCategoryContainer heading="Category">
      {filteredItems.map((item) => (
        <PostCategoryItem 
          key={item.key}
          onInputChange={onInputChange}
          check={postCategory}
          item={item}
          inputKey="category"
        />
      ))}
    </PostCategoryContainer>
  );
}


export function PSLightSwitch({ label, name, onChange, value = false }) {
  const [checked, setChecked] = useState(value);

  useEffect(() => {
    setChecked(value);
  }, [value]);

  const handleChange = (newValue) => {
    setChecked(newValue);
    onChange(name, newValue);
  };

  return (
    <>
      <PSLabel value={label} />
      <div className="flex items-center gap-2">
        <Switch.Root
          checked={checked}
          onCheckedChange={handleChange}
          className="relative w-10 h-5 bg-secondary rounded-full transition duration-300 
                    outline-none cursor-pointer"
        >
          <Switch.Thumb
            className="absolute left-1 top-1 w-3 h-3 bg-brg rounded-full 
                        transition-transform duration-300 translate-x-0 
                        data-[state=checked]:translate-x-5 data-[state=checked]:bg-tangerine"
          />
        </Switch.Root>
      </div>
    </>
  );
}


export function PSFileUpload({
  onChange,
  name,
  label,
  context,
  value = '',
  acceptedTypes = '.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt',
  maxFileSizeMB = 10,
  showPreview = true
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (fileSizeMB > maxFileSizeMB) {
      alert(`File size exceeds ${maxFileSizeMB}MB limit.`);
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);

    // Create preview for images
    if (showPreview && selectedFile.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(selectedFile));
    } else {
      setPreview(null);
    }

    onChange(context, selectedFile);
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
    setFileName('');
    onChange(context, '');
    
    // Clear the input
    const input = document.getElementById(name);
    if (input) {
      input.value = '';
    }
  };

  useEffect(() => {
    if (value && typeof value === 'string' && value.length > 0) {
      setFileName(value);
    } else {
      setFileName('');
    }
  }, [value]);

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'txt':
        return '📄';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📎';
    }
  };

  return (
    <div className="my-5">
      <PSLabel value={label} />

      {(file || fileName) ? (
        <div className="bg-secondary inline-block p-3 px-6 text-center rounded-default border border-inputBorder">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xl">{getFileIcon(fileName || file?.name)}</span>
            <div>
              <p className=" text-sm">{fileName || file?.name}</p>
              {file && (
                <p className="text-xs text-gray-500">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              )}
            </div>
          </div>
          <Button variant="destructive" onClick={removeFile} size="sm">
            Remove
          </Button>
        </div>
      ) : (
        <>
          <label 
            htmlFor={name} 
            className="inline-block text-black border-[2px] border-black  py-2 px-4 rounded-lg cursor-pointer hover:bg-black hover:text-white transition-colors"
          >
            Upload File
          </label>
          <input
            type="file"
            accept={acceptedTypes}
            id={name}
            onChange={handleFileChange}
            className="hidden"
          />
          <p className="mt-3  text-[11px] text-guards">
            File limitations: {maxFileSizeMB}MB max. Accepted formats: {acceptedTypes.replace(/\./g, '').toUpperCase()}
          </p>
        </>
      )}

      {preview && (
        <div className="mt-3">
          <img 
            src={preview} 
            alt="File preview" 
            className="max-w-[400px] max-h-[300px] object-contain rounded-lg border border-inputBorder"
          />
        </div>
      )}
    </div>
  );
}
